export interface ApiErrorBody {
  error?: unknown;
  message?: unknown;
  details?: unknown;
  code?: unknown;
  field?: unknown;
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly details?: unknown;
  readonly code?: string;
  readonly field?: string;

  constructor(
    message: string,
    status: number,
    details?: unknown,
    refusal?: { code?: string; field?: string },
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.details = details;
    this.code = refusal?.code;
    this.field = refusal?.field;
  }
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

export function readApiErrorMessage(data: ApiErrorBody | null): string {
  if (typeof data?.message === 'string' && data.message) return data.message;
  if (typeof data?.error === 'string' && data.error) return data.error;
  return 'API request failed';
}

function traceSuffix(details: unknown): string | null {
  const values = record(details);
  if (!values) return null;
  const fields = [
    ['trace', values.traceId],
    ['operation', values.operation],
    ['upstream', values.upstreamRequestId],
  ].filter((entry): entry is [string, string] => typeof entry[1] === 'string' && entry[1].trim().length > 0);
  return fields.length ? fields.map(([label, value]) => `${label} ${value.trim()}`).join('; ') : null;
}

function messageWithDetails(data: ApiErrorBody): string {
  const primary = readApiErrorMessage(data);
  const suffix = traceSuffix(data.details);
  if (suffix) return `${primary} (${suffix})`;
  if (data.details === undefined) return primary;
  try {
    return `${primary} (${JSON.stringify(data.details)})`;
  } catch {
    return primary;
  }
}

const refusalListeners = new Map<string, Set<(error: ApiRequestError) => void>>();

/** Calls `listener` whenever any request is refused with `code`, wherever the caller handles the error; returns the unsubscribe. */
export function onRefusalCode(code: string, listener: (error: ApiRequestError) => void): () => void {
  const listeners = refusalListeners.get(code) ?? new Set();
  refusalListeners.set(code, listeners.add(listener));
  return () => { listeners.delete(listener); };
}

export function apiRequestErrorFromBody(value: unknown, status: number): ApiRequestError {
  const data = record(value) ?? {};
  const error = new ApiRequestError(
    messageWithDetails(data),
    status,
    data.details,
    {
      code: typeof data.code === 'string' ? data.code : undefined,
      field: typeof data.field === 'string' ? data.field : undefined,
    },
  );
  if (error.code) refusalListeners.get(error.code)?.forEach((listener) => listener(error));
  return error;
}
